/**
 * @file: MediaSendStylePanel.tsx
 *
 * Asks how a multi-image/video selection should be sent: one-by-one (each its
 * own message) or all-in-one as an album in a chosen bubble layout. Opened
 * after a ≥2 pick; the composer owns the pending assets and dispatches on
 * confirm. The grid layout is offered only at ≥9 items (it collapses the
 * overflow to a `+N` tile).
 */

import { Typography } from '@euler/app/components/typography';
import { BottomPanel } from '@euler/components/BottomPanel';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChatMediasLayout } from '@euler/model/chat';
import {
  Check,
  Layers,
  LayoutGrid,
  LucideIcon,
  Rows3,
} from 'lucide-react-native';
import { FC, memo, useEffect, useMemo, useState } from 'react';
import { ScrollView, TouchableOpacity, View } from 'react-native';

/** The chosen send mode: separate messages, or one album with a layout. */
export type MediaSendStyle =
  | { mode: 'separate' }
  | { mode: 'album'; layout: ChatMediasLayout };

/** The grid layout only makes sense once the album is large. */
const GRID_MIN_ITEMS = 9;

type OptionId = 'separate' | 'stack' | 'grid';

const OPTIONS: Array<{
  id: OptionId;
  label: string;
  hint: string;
  Icon: LucideIcon;
  /** Minimum item count for this option to be offered. */
  minItems?: number;
}> = [
  { id: 'separate', label: '逐条发送', hint: '每张单独成条', Icon: Rows3 },
  { id: 'stack', label: '堆叠', hint: '卡片堆叠样式', Icon: Layers },
  {
    id: 'grid',
    label: '宫格',
    hint: '九宫格，多余折叠为 +N',
    Icon: LayoutGrid,
    minItems: GRID_MIN_ITEMS,
  },
];

const STYLE_OF: Record<OptionId, MediaSendStyle> = {
  separate: { mode: 'separate' },
  stack: { mode: 'album', layout: 'stack' },
  grid: { mode: 'album', layout: 'grid' },
};

export const MediaSendStylePanel: FC<{
  visible: boolean;
  assetCount: number;
  onClose: () => void;
  onConfirm: (style: MediaSendStyle) => void;
}> = memo(({ visible, assetCount, onClose, onConfirm }) => {
  const { rpx } = useResponsiveContext();
  const [selected, setSelected] = useState<OptionId>('separate');

  // Each open starts on the default (逐条发送).
  useEffect(() => {
    if (visible) setSelected('separate');
  }, [visible]);

  const options = useMemo(
    () => OPTIONS.filter(o => assetCount >= (o.minItems ?? 0)),
    [assetCount],
  );

  return (
    <BottomPanel visible={visible} onClose={onClose} fitContent>
      <View style={{ paddingTop: rpx(20), paddingBottom: rpx(16) }}>
        <View style={{ paddingHorizontal: rpx(24), marginBottom: rpx(12) }}>
          <Typography.Text
            size={rpx(16)}
            color="#0F172A"
            style={{ fontFamily: FontFamily.NotoSans.Medium }}
          >
            发送方式
          </Typography.Text>
          <Typography.Text
            size={rpx(12)}
            color="#94A3B8"
            style={{ marginTop: rpx(2) }}
          >
            已选择 {assetCount} 项
          </Typography.Text>
        </View>

        <ScrollView
          style={{ maxHeight: rpx(360) }}
          showsVerticalScrollIndicator={false}
        >
          {options.map(({ id, label, hint, Icon }) => {
            const active = selected === id;
            return (
              <TouchableOpacity
                key={id}
                activeOpacity={0.7}
                onPress={() => setSelected(id)}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  paddingHorizontal: rpx(24),
                  paddingVertical: rpx(12),
                }}
              >
                <View
                  style={{
                    width: rpx(40),
                    height: rpx(40),
                    borderRadius: rpx(12),
                    backgroundColor: active ? '#EFF6FF' : '#F1F5F9',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon size={rpx(20)} color={active ? '#2563EB' : '#475569'} />
                </View>
                <View style={{ flex: 1, marginLeft: rpx(14) }}>
                  <Typography.Text
                    size={rpx(15)}
                    color="#0F172A"
                    style={{ fontFamily: FontFamily.NotoSans.Medium }}
                  >
                    {label}
                  </Typography.Text>
                  <Typography.Text
                    size={rpx(12)}
                    color="#94A3B8"
                    style={{ marginTop: rpx(2) }}
                  >
                    {hint}
                  </Typography.Text>
                </View>
                <View
                  style={{
                    width: rpx(20),
                    height: rpx(20),
                    borderRadius: rpx(10),
                    borderWidth: active ? 0 : 1.5,
                    borderColor: '#CBD5E1',
                    backgroundColor: active ? '#2563EB' : 'transparent',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {active ? <Check size={rpx(13)} color="#fff" /> : null}
                </View>
              </TouchableOpacity>
            );
          })}
        </ScrollView>

        <View style={{ paddingHorizontal: rpx(24), marginTop: rpx(12) }}>
          <TouchableOpacity
            activeOpacity={0.85}
            onPress={() => onConfirm(STYLE_OF[selected])}
            style={{
              height: rpx(48),
              borderRadius: rpx(24),
              backgroundColor: '#2563EB',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography.Text
              size={rpx(15)}
              color="#fff"
              style={{ fontFamily: FontFamily.NotoSans.Medium }}
            >
              发送
            </Typography.Text>
          </TouchableOpacity>
        </View>
      </View>
    </BottomPanel>
  );
});
